import { useMemo, useState } from "react";

import { SanitizedTag } from "../utils";

interface ArtworkProduct {
  id: number | string;
  pictureLargeUrl?: string;
  url?: string;
  name?: string;
  categoryName?: string;
}

const ArtworkCard = ({ product }: { product: ArtworkProduct }) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  // Bumped on every retry click. Forces a fresh fetch by appending a cache-
  // busting query param to the src AND remounting the <img> so onError/onLoad
  // fire again cleanly.
  const [retryNonce, setRetryNonce] = useState(0);
  // Once the image has loaded, switch the frame's aspect ratio to the image's
  // natural ratio so the artwork is shown in full (no cropping) and the
  // surrounding card adapts to the artwork rather than the artwork being
  // squeezed into a fixed placeholder ratio.
  const [naturalAspectRatio, setNaturalAspectRatio] = useState<
    string | undefined
  >(undefined);

  const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;

    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      setNaturalAspectRatio(`${img.naturalWidth} / ${img.naturalHeight}`);
    }
    setLoaded(true);
  };

  const handleRetry = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Don't let the click bubble to the parent <a> and open the product page.
    event.preventDefault();
    event.stopPropagation();
    setErrored(false);
    setLoaded(false);
    setRetryNonce((n) => n + 1);
  };

  // Use key={retryNonce} on the <img> below to force React to remount the
  // element on retry, which re-fires the browser fetch + onLoad/onError. We do
  // NOT mangle the URL with a cache-bust query string because pictureLargeUrl
  // may carry a signed-URL signature in the future and arbitrary query params
  // would invalidate the signature.
  const imgSrc = product.pictureLargeUrl || "";

  return (
    <div className="artwork-card" key={product.id}>
      <a
        href={product.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block artwork-card-frame"
        style={
          naturalAspectRatio ? { aspectRatio: naturalAspectRatio } : undefined
        }
      >
        {!loaded && !errored && (
          <div
            className="artwork-card-skeleton"
            aria-hidden="true"
            data-testid="artwork-card-skeleton"
          />
        )}
        {errored && (
          <div
            className="artwork-card-error"
            role="alert"
            data-testid="artwork-card-error"
          >
            <svg
              className="artwork-card-error-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="9" cy="9" r="1.5" />
              <path d="m21 15-5-5L5 21" />
            </svg>
            <p className="artwork-card-error-text">
              {`Couldn't load image${
                product?.name ? ` "${product.name}"` : ""
              }.`}
            </p>
            <button
              type="button"
              className="artwork-card-error-retry"
              onClick={handleRetry}
              aria-label="Try loading the image again"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 12a9 9 0 1 1-3-6.7" />
                <path d="M21 3v6h-6" />
              </svg>
              Try again
            </button>
          </div>
        )}
        {!errored && (
          <img
            key={retryNonce}
            src={imgSrc}
            alt={product?.name || "Artwork"}
            loading="lazy"
            decoding="async"
            onLoad={handleLoad}
            onError={() => setErrored(true)}
            className={`artwork-card-img hover:opacity-90 transition-opacity cursor-pointer ${
              loaded ? "is-loaded" : ""
            }`}
          />
        )}
      </a>
    </div>
  );
};

export const SignatureProfileGallery = ({ profile }: { profile: any }) => {
  const products = useMemo(
    () => (Array.isArray(profile?.products) ? profile.products : []),
    [profile?.products]
  );

  const categories = useMemo(() => {
    const unique: string[] = [];

    products.forEach((p: any) => {
      const category = p?.categoryName;

      if (category && !unique.includes(category)) unique.push(category);
    });

    return unique;
  }, [products]);

  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const filteredProducts = useMemo(() => {
    if (selectedCategory === "all") {
      return products;
    }

    return products.filter((p: any) => p?.categoryName === selectedCategory);
  }, [products, selectedCategory]);

  const leadContent: string | undefined = profile?.leadContent;
  const hasProducts = products.length > 0;

  if (!hasProducts) return null;

  return (
    <section className="py-12 bg-white">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <div>
            <h2 className="font-playfair text-3xl font-bold mb-2">
              Portfolio Gallery
            </h2>
            {/* 11. Signature Profile portfolio gallery lead_content */}
            {leadContent ? (
              <SanitizedTag
                html={leadContent}
                tag="p"
                className="text-muted-foreground"
              />
            ) : null}
          </div>
          {categories.length > 0 ? (
            <div className="mt-4 flex justify-end">
              {/* 12. Signature Profile portfolio gallery filter button (filter by signature profile product categories) */}
              <div className="flex gap-2">
                <button
                  className={`inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 px-3 rounded-full capitalize ${
                    selectedCategory === "all"
                      ? "bg-black text-white hover:bg-black/90 border border-black"
                      : "border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                  }`}
                  onClick={() => setSelectedCategory("all")}
                  type="button"
                  aria-pressed={selectedCategory === "all"}
                >
                  all
                </button>
                {categories.map((category) => (
                  <button
                    key={category}
                    className={`inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 px-3 rounded-full capitalize ${
                      selectedCategory === category
                        ? "bg-black text-white hover:bg-black/90 border border-black"
                        : "border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                    }`}
                    onClick={() => setSelectedCategory(category)}
                    type="button"
                    aria-pressed={selectedCategory === category}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        {/* 13. Signature Profile portfolio gallery images (signature_profile.products) */}
        {filteredProducts.length > 0 ? (
          <div className="gallery-masonry">
            {filteredProducts.map((product: any) =>
              product?.pictureLargeUrl ? (
                <ArtworkCard product={product} key={product.id} />
              ) : null
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default SignatureProfileGallery;
